import { createAction } from 'redux-actions';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import { EditorState } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import {
  UploadDocumentPayload,
  UploadFilePayload,
  LegalDocumentType,
  UploadError,
  UploadAttachmentPayload,
  AttachmentType,
} from './types';
import { actionKey } from './constants';

let fileUploadIndex = 0;
const addUploadId = payload => ({
  ...payload,
  uploadId: ++fileUploadIndex,
});

export const uploadFile = createAction(
  `${actionKey}/UPLOAD`,
  (payload: Omit<UploadFilePayload, 'uploadId'>) => addUploadId(payload)
);
export const uploadFiles = createAction(
  `${actionKey}/UPLOAD_MULTI`,
  (files: Omit<UploadFilePayload, 'uploadId'>[]) => ({
    files: files.map(addUploadId),
  })
);
export const cancelFileUpload = createAction(
  `${actionKey}/UPLOAD_CANCEL`,
  (payload: Pick<UploadFilePayload, 'uploadId'>) => payload
);
export const uploadSuccess = createAction(
  `${actionKey}/UPLOAD/SUCCESS`,
  (payload: { uploadId: number; deviationId: number; deviation?: PapiDeviation }) => payload
);
export const uploadFailure = createAction(
  `${actionKey}/UPLOAD/FAILURE`,
  (uploadId: number, error: UploadError) => ({ uploadId, error })
);
export const uploadProgress = createAction(
  `${actionKey}/UPLOAD/PROGRESS`,
  (uploadId: number, progress: number) => ({ uploadId, progress })
);
export const uploadFileImmediately = createAction(
  `${actionKey}/UPLOAD/IMMEDIATE`,
  (payload: UploadFilePayload) => payload
);
export const clearInactiveUploads = createAction(`${actionKey}/CLEAR_INACTIVE_UPLOADS`, () => ({}));

export const uploadAttachment = createAction(
  `${actionKey}/UPLOAD_ATTACHMENT`,
  (payload: Omit<UploadAttachmentPayload, 'uploadId'>) => addUploadId(payload)
);
export const uploadAttachmentImmediately = createAction(
  `${actionKey}/UPLOAD_ATTACHMENT/IMMEDIATE`,
  (payload: UploadAttachmentPayload) => payload
);
export const uploadAttachmentProgress = createAction(
  `${actionKey}/UPLOAD_ATTACHMENT/PROGRESS`,
  (uploadId: number, progress: number) => ({ uploadId, progress })
);
export const uploadAttachmentSuccess = createAction(
  `${actionKey}/UPLOAD_ATTACHMENT/SUCCESS`,
  (payload: {
    uploadId: number;
    fileId: number;
    deviationId: number;
    file: File;
    type: AttachmentType;
    attachment?: PapiAttachmentFile;
    deviation?: PapiDeviation;
  }) => payload
);
export const uploadAttachmentFailure = createAction(
  `${actionKey}/UPLOAD_ATTACHMENT/FAILURE`,
  (payload: { uploadId: number; type: AttachmentType; error: UploadError }) => payload
);
export const cancelAttachmentUpload = createAction(
  `${actionKey}/UPLOAD_ATTACHMENT/CANCEL`,
  (payload: Pick<UploadAttachmentPayload, 'uploadId' | 'type'>) => payload
);
export const removeAttachment = createAction(
  `${actionKey}/UPLOAD_ATTACHMENT/REMOVE`,
  (fileId: number, deviationId: number, type: AttachmentType, onSuccess: () => void) => ({
    fileId,
    deviationId,
    type,
    onSuccess,
  })
);
export const repositionAttachment = createAction(
  `${actionKey}/ATTACHMENT/REPOSITION`,
  (fileId: number, deviationId: number, type: string, position: number) => ({
    fileId,
    deviationId,
    type,
    position,
  })
);

export const removeAttachments = createAction(
  `${actionKey}/UPLOAD_ATTACHMENTS/REMOVE`,
  (fileIds: number[], deviationId: number, type: AttachmentType) => ({
    fileIds,
    deviationId,
    type,
  })
);

export const attachmentDeviationLock = createAction(
  `${actionKey}/UPLOAD_ATTACHMENTS/DEVIATION_LOCK`,
  (deviationId: number) => ({
    deviationId,
  })
);

export const attachmentDeviationUnlock = createAction(
  `${actionKey}/UPLOAD_ATTACHMENTS/DEVIATION_UNLOCK`,
  (deviationId: number) => ({
    deviationId,
  })
);

export const setAttachmentCaption = createAction(
  `${actionKey}/ATTACHMENT/SET_CAPTION`,
  (fileId: number, deviationId: number, type: string, caption: EditorState) => ({
    fileId,
    deviationId,
    type,
    caption,
  })
);
export const setMainDeviationCaption = createAction(
  `${actionKey}/MAIN_DEVIATION/SET_CAPTION`,
  (deviationId: number, caption: EditorState) => ({
    deviationId,
    caption,
  })
);

export const uploadDocument = createAction(
  `${actionKey}/UPLOAD_DOCUMENT`,
  (payload: UploadDocumentPayload) => payload
);
export const uploadDocumentProgress = createAction(
  `${actionKey}/UPLOAD_DOCUMENT/PROGRESS`,
  (documentType: LegalDocumentType, progress: number) => ({
    documentType,
    progress,
  })
);
export const uploadDocumentSuccess = createAction(
  `${actionKey}/UPLOAD_DOCUMENT/SUCCESS`,
  (documentType: LegalDocumentType, documentUrl: string) => ({
    documentType,
    documentUrl,
  })
);
export const uploadDocumentFailure = createAction(
  `${actionKey}/UPLOAD_DOCUMENT/FAILURE`,
  (documentType: LegalDocumentType, error: UploadError) => ({
    documentType,
    error,
  })
);
export const cancelDocumentUpload = createAction(
  `${actionKey}/UPLOAD_DOCUMENT/CANCEL`,
  (documentType: LegalDocumentType) => ({ documentType })
);
